import { CallState } from "@b2bportal/core-types";
import { FixedPointsPricingVersion } from "@b2bportal/rbc-accounts-api";
import { createSelector } from "@reduxjs/toolkit";
import type { GridV2State, GridV2Version } from "./types";

const getGridV2 = (state: GridV2State) => state.gridV2;

/**
 * The grid this user is priced on. V1 unless the BFF said V2: before the
 * first answer, or when it failed, there is no V2 merchandising (R6). A
 * refresh in flight or failed keeps the version already granted.
 */
export const getGridV2EffectiveVersion = createSelector(
  getGridV2,
  (gridV2): GridV2Version =>
    gridV2.effectiveVersion === FixedPointsPricingVersion.V2
      ? FixedPointsPricingVersion.V2
      : FixedPointsPricingVersion.V1
);

export const getIsGridV2OptedOut = createSelector(
  getGridV2,
  (gridV2) => gridV2.isOptedOut
);

/**
 * Whether this user is in the Grid V2 variant of the A/B test. Sticky: an
 * opted-out user stays in the variant (only variant users can opt out) but
 * is priced on V1, so the V2 elements gate on `getIsPricedOnGridV2`, not on
 * this.
 */
export const getIsGridV2 = createSelector(
  getGridV2EffectiveVersion,
  getIsGridV2OptedOut,
  (version, isOptedOut) =>
    version === FixedPointsPricingVersion.V2 || isOptedOut
);

/** What every Grid V2 element gates on: V2 pricing, so not after an opt-out. */
export const getIsPricedOnGridV2 = createSelector(
  getGridV2EffectiveVersion,
  (version) => version === FixedPointsPricingVersion.V2
);

/**
 * Whether the BFF has answered (success or failure). The intro modals wait
 * on this so they are decided before the first results render.
 */
export const getIsGridV2Resolved = createSelector(
  getGridV2,
  (gridV2) =>
    gridV2.callState === CallState.Success ||
    gridV2.callState === CallState.Failed
);
